import React, { useEffect, useRef, useState } from 'react';
import { website_whats_new } from '../services';
import { subHeading3, body, bodySmall, label, caption, fontWeight, spacing } from '../Constants/Fonts';
import { lightTheme } from '../Constants/Colors';

// All styling is inline (no SCSS). Typography comes from the Fonts.js presets
// ('Outfit', sans-serif) + spacing scale; colours come from the light theme
// (lightTheme) in Colors.js. Only fixed layout dimensions are literal.
const S = {
    wrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    btn: {
        background: lightTheme.surface, border: `1px solid ${lightTheme.border}`, padding: 0, cursor: 'pointer',
        color: lightTheme.textSecondary, width: 38, height: 38, borderRadius: lightTheme.radius,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s ease',
    },
    panel: {
        position: 'absolute', top: 50, right: 0, width: 380, maxHeight: 520,
        background: lightTheme.surface, border: `1px solid ${lightTheme.border}`, borderRadius: spacing.xl,
        boxShadow: lightTheme.shadowLg, zIndex: 50,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${lightTheme.border}`, background: lightTheme.surfaceMuted,
    },
    panelTitle: subHeading3({ fontWeight: fontWeight.bold, color: lightTheme.textPrimary }),
    panelBody: { flex: 1, overflowY: 'auto' },
    state: body({
        padding: `${spacing['4xl']}px ${spacing['3xl']}px`,
        textAlign: 'center', color: lightTheme.textMuted,
    }),
    stateError: body({
        padding: `${spacing['4xl']}px ${spacing['3xl']}px`,
        textAlign: 'center', color: lightTheme.danger,
    }),
    item: {
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${lightTheme.border}`,
    },
    meta: {
        display: 'flex', alignItems: 'center', gap: spacing.lg,
        marginBottom: spacing.md, flexWrap: 'wrap',
    },
    tag: caption({
        fontWeight: fontWeight.bold, background: lightTheme.successSoft, color: lightTheme.success,
        padding: `${spacing.xs}px ${spacing.md}px`, borderRadius: spacing.sm, letterSpacing: '0.4px',
    }),
    category: label({
        fontWeight: fontWeight.semibold, color: lightTheme.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.3px',
    }),
    date: label({ fontWeight: fontWeight.regular, color: lightTheme.textMuted, marginLeft: 'auto' }),
    title: body({ fontWeight: fontWeight.semibold, color: lightTheme.textPrimary, marginBottom: spacing.sm, lineHeight: 1.35 }),
    desc: bodySmall({ color: lightTheme.textSecondary, lineHeight: 1.5, marginBottom: spacing.lg }),
};

const WhatsNewPopover = () => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [btnHover, setBtnHover] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await website_whats_new();
            const data = res?.statusCode?.response_data?.data;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching whats-new:', err);
            setError('Could not load updates.');
        } finally {
            setLoading(false);
            setFetched(true);
        }
    };

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !fetched) loadItems();
    };

    const formatDate = (raw) => {
        if (!raw) return '';
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return raw;
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div style={S.wrapper} ref={wrapperRef}>
            <button
                type="button"
                style={{
                    ...S.btn,
                    background: btnHover ? lightTheme.surfaceHover : lightTheme.surface,
                    borderColor: btnHover ? lightTheme.borderStrong : lightTheme.border,
                }}
                onClick={handleToggle}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                aria-label="What's new"
            >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="3 11 22 2 13 21 11 13 3 11" />
                </svg>
            </button>

            {open && (
                <div style={S.panel}>
                    <div style={S.header}>
                        <span style={S.panelTitle}>What's New</span>
                    </div>

                    <div style={S.panelBody}>
                        {loading ? (
                            <div style={S.state}>Loading…</div>
                        ) : error ? (
                            <div style={S.stateError}>{error}</div>
                        ) : items.length === 0 ? (
                            <div style={S.state}>Nothing new yet.</div>
                        ) : (
                            items.map((item, idx) => (
                                <div key={`${item.date}-${idx}`} style={S.item}>
                                    <div style={S.meta}>
                                        {item.tag && <span style={S.tag}>{item.tag}</span>}
                                        {item.category && <span style={S.category}>{item.category}</span>}
                                        {item.date && <span style={S.date}>{formatDate(item.date)}</span>}
                                    </div>
                                    {item.title && <div style={S.title}>{item.title}</div>}
                                    {item.description && <div style={S.desc}>{item.description}</div>}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsNewPopover;
